import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Loader2, AlertCircle, Calendar, User, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

/**
 * Pense-bête : liste les RDV individuels passés sans synthèse
 * pour l'intervenant connecté.
 *
 * @param {Function} onSelectAction  - Callback(action) ouvre le dialog d'édition pour saisir la synthèse.
 */
const SynthesesManquantes = ({ onSelectAction }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total: 0, actions: [] });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/actions/syntheses-manquantes');
      setData(res.data);
    } catch (error) {
      toast.error('Erreur de chargement du pense-bête');
    } finally {
      setLoading(false);
    }
  };

  const daysAgo = (dateStr) => {
    try {
      const d = new Date(dateStr);
      const today = new Date();
      const diff = Math.floor((today - d) / (1000 * 60 * 60 * 24));
      if (diff === 0) return "Aujourd'hui";
      if (diff === 1) return 'Hier';
      return `il y a ${diff} jours`;
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <Card className="bg-white border-amber-200 rounded-3xl p-6">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
        </div>
      </Card>
    );
  }

  if (data.total === 0) {
    return (
      <Card className="bg-emerald-50 border-emerald-200 rounded-3xl">
        <CardContent className="p-5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
            ✓
          </div>
          <div>
            <p className="font-semibold text-emerald-900">Tout est à jour !</p>
            <p className="text-sm text-emerald-700">Aucune synthèse en attente.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-amber-50 border-amber-200 rounded-3xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-amber-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
          <AlertCircle className="w-5 h-5" />
          Synthèses à compléter
          <Badge className="ml-2 bg-amber-600 text-white">{data.total}</Badge>
        </CardTitle>
        <p className="text-sm text-amber-700 mt-1">
          RDV passés sans synthèse rédigée. Cliquez pour les compléter.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {data.actions.slice(0, 10).map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onSelectAction && onSelectAction(a)}
            className="w-full text-left bg-white border border-amber-200 rounded-xl p-3 hover:border-amber-400 hover:shadow-sm transition-all flex items-center justify-between gap-3"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <User className="w-4 h-4 text-amber-700" />
                {a.apprenant_prenom} {a.apprenant_nom}
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(a.date).toLocaleDateString('fr-FR')} ({daysAgo(a.date)})
                </span>
                <span>{a.heure_debut}–{a.heure_fin}</span>
                {a.titre && <span className="italic truncate">{a.titre}</span>}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-amber-600 flex-shrink-0" />
          </button>
        ))}
        {data.total > 10 && (
          <p className="text-xs text-amber-700 text-center pt-2">
            … et {data.total - 10} autre{data.total - 10 > 1 ? 's' : ''} RDV
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default SynthesesManquantes;
